import React, { useState, useCallback } from "react";
import {
  Identifier,
  Button,
  SimpleForm,
  SaveContextProvider,
  Record,
  RedirectionSideEffect,
  OnSuccess,
  TransformData,
  OnFailure,
  required,
  Toolbar,
  SaveButton,
  useTranslate,
} from "react-admin";
import AddIcon from "@material-ui/icons/Add";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DateTimePicker } from "@material-ui/pickers";
import { Divider, Paper } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import "./NewShareLink.css";
import { CreateLiveTrackTokenDto } from "../../../common/data/service-proxies";
import { parseISO } from "date-fns";

interface Props {
  vehicleId: Identifier;
  onSaved: (shareLink: CreateLiveTrackTokenDto) => void;
}

interface ToolbarProps {
  onClick: () => void;
}

const NewLinkToolbar = ({ onClick, ...props }: ToolbarProps) => {
  const translate = useTranslate();
  return (
    <Toolbar {...props} className="new-link-toolbar">
      <SaveButton
        label={translate("resources.locations-current.labels.generate")}
      />
      <Button label="ra.action.cancel" onClick={onClick}>
        <CloseIcon />
      </Button>
    </Toolbar>
  );
};

const mustBeInFuture = (value: string) => {
  if (new Date().getTime() > parseISO(value).getTime()) {
    return "resources.locations-current.labels.mustBeInFuture";
  }

  return;
};

export const NewShareLink = ({ vehicleId, onSaved }: Props) => {
  const [adding, setAdding] = useState(false);
  const translate = useTranslate();

  const handleSave = useCallback(
    (
      record: Partial<Record>,
      redirect: RedirectionSideEffect,
      callbacks?: {
        onSuccess?: OnSuccess;
        onFailure?: OnFailure;
        transform?: TransformData;
      }
    ) => {
      setAdding(false);
      onSaved({ ...record, vehicleId } as CreateLiveTrackTokenDto);
    },
    [onSaved, vehicleId]
  );

  if (!adding) {
    return (
      <Button
        label={translate("resources.locations-current.labels.generateNew")}
        onClick={() => setAdding(true)}>
        <AddIcon />
      </Button>
    );
  }

  return (
    <div className="share-link-form">
      <Paper elevation={2}>
        <SaveContextProvider
          value={{ save: handleSave, setOnFailure: () => {} }}>
          <SimpleForm
            variant="outlined"
            save={handleSave}
            toolbar={<NewLinkToolbar onClick={() => setAdding(false)} />}>
            <GenericDateOrTimePicker
              PickerComponent={DateTimePicker}
              source="validFrom"
              label={translate("resources.locations-current.labels.validFrom")}
              validate={required()}
              fullWidth
            />
            <GenericDateOrTimePicker
              PickerComponent={DateTimePicker}
              source="validTo"
              label={translate("resources.locations-current.labels.validTo")}
              validate={[required(), mustBeInFuture]}
              fullWidth
            />
          </SimpleForm>
        </SaveContextProvider>
        <Divider />
      </Paper>
    </div>
  );
};
